// Drawer displaying Governance intelligence for a document.
import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import { DataTable } from "../../../components/DataTable";
import { StatTile } from "../Domain";
import { formatPercent } from "../utils";
import type {
  GovernanceDisplayData,
  GovernanceResult,
} from "./governanceTypes";
import {
  ApprovalRow,
  GovernanceDrawerHeader,
  GovernanceMissingBanner,
  OwnershipCard,
  PrimaryGovernanceHighlight,
  ReviewCycleRow,
  SectionCard,
} from "./governanceDrawerParts";
import {
  adaptGovernanceResult,
  controlColumns,
  countMissingControls,
  findingColumns,
  sortFindingsBySeverity,
  GOVERNANCE_CONTROLS_MAX_HEIGHT,
  GOVERNANCE_CONTROLS_PAGE_SIZE,
  GOVERNANCE_DRAWER_COPY,
  GOVERNANCE_DRAWER_OVERLAY_CLS,
  GOVERNANCE_DRAWER_SHEET_CLS,
  GOVERNANCE_FINDINGS_MAX_HEIGHT,
  GOVERNANCE_FINDINGS_PAGE_SIZE,
  GOVERNANCE_SECTION_COPY,
} from "./utilities";

export interface GovernanceDrawerProps {
  open: boolean;
  onClose: () => void;
  result?: GovernanceResult | null;
  documentTitle?: string;
  documentType?: string;
}

export function GovernanceDrawer({
  open,
  onClose,
  result,
  documentTitle,
  documentType,
}: GovernanceDrawerProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onClose();
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName={GOVERNANCE_DRAWER_OVERLAY_CLS}
        className={GOVERNANCE_DRAWER_SHEET_CLS}
      >
        <SheetTitle className="sr-only">{GOVERNANCE_DRAWER_COPY.title}</SheetTitle>
        <SheetDescription className="sr-only">
          {GOVERNANCE_DRAWER_COPY.description}
        </SheetDescription>
        {!result ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
            <span className="text-sm">{GOVERNANCE_DRAWER_COPY.loading}</span>
          </div>
        ) : (
          <GovernanceContent
            /* Remounting on document change resets paging without a useEffect. */
            key={result.documentId ?? "governance"}
            raw={result}
            documentTitle={documentTitle}
            documentType={documentType}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

interface GovernanceContentProps {
  raw: GovernanceResult;
  documentTitle?: string;
  documentType?: string;
}

function GovernanceContent({
  raw,
  documentTitle,
  documentType,
}: GovernanceContentProps) {
  // ── Local UI state ──────────────────────────────────────────────
  const [controlsPage, setControlsPage] = useState(0);
  const [findingsPage, setFindingsPage] = useState(0);

  // ── Derived data (raw → display) ─────────────────────────────
  const data: GovernanceDisplayData = useMemo(
    () => adaptGovernanceResult(raw, documentTitle, documentType),
    [raw, documentTitle, documentType],
  );

  const { summary, ownership, approvals, review_cycles: reviewCycles, controls, findings } =
    data;
  const overallPct = formatPercent(summary.governance_score);
  const missingCount = countMissingControls(controls);
  const sortedFindings = sortFindingsBySeverity(findings);

  const controlsPresent = summary.controls_present;
  const controlsExpected = summary.controls_expected;
  const strongControls = summary.strong_controls;
  const namedOwners = summary.named_owners;

  return (
    <>
      <GovernanceDrawerHeader
        documentTitle={documentTitle}
        documentType={documentType}
        documentMeta={data.document}
        backendNote={data.backendNote}
        overallPct={overallPct}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-4">
        <GovernanceMissingBanner count={missingCount} />

        <SectionCard
          headerLabel={GOVERNANCE_SECTION_COPY.summary.header}
          rightLabel={GOVERNANCE_SECTION_COPY.summary.right}
        >
          <GovernanceSummaryTiles
            controlsPresent={controlsPresent}
            controlsExpected={controlsExpected}
            strongControls={strongControls}
            namedOwners={namedOwners}
            reviewCyclesDefined={summary.review_cycles_defined}
            overallPct={overallPct}
          />
        </SectionCard>

        <SectionCard
          headerLabel={GOVERNANCE_SECTION_COPY.highlight.header}
          rightLabel={GOVERNANCE_SECTION_COPY.highlight.right}
        >
          <PrimaryGovernanceHighlight
            overallPct={overallPct}
            strongControls={strongControls}
            weakControls={summary.weak_controls}
            missing={summary.controls_missing}
            namedOwners={namedOwners}
            approvers={summary.approvers_identified}
            cycles={summary.review_cycles_defined}
          />
        </SectionCard>

        <SectionCard
          headerLabel={GOVERNANCE_SECTION_COPY.ownership.header}
          rightLabel={`${ownership.length} roles named`}
        >
          <div className="grid gap-3 p-4 md:grid-cols-2">
            {ownership.map((o, i) => (
              <OwnershipCard
                key={`${o.role_type ?? "role"}-${o.role ?? ""}-${i}`}
                item={o}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={GOVERNANCE_SECTION_COPY.approvals.header}
          rightLabel={`${approvals.length} approval records`}
        >
          <div className="flex flex-col gap-2 p-4">
            {approvals.map((a, i) => (
              <ApprovalRow
                key={`${a.authority ?? "authority"}-${a.approval_type ?? ""}-${i}`}
                item={a}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={GOVERNANCE_SECTION_COPY.reviewCycles.header}
          rightLabel={`${reviewCycles.length} cycles`}
        >
          <div className="flex flex-col gap-2 p-4">
            {reviewCycles.map((r, i) => (
              <ReviewCycleRow key={`${r.activity ?? "cycle"}-${i}`} item={r} />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={GOVERNANCE_SECTION_COPY.controls.header}
          rightLabel={`${controlsPresent} of ${controlsExpected} present`}
        >
          <div className="p-3">
            <DataTable
              columns={controlColumns}
              data={controls}
              compact
              maxHeight={GOVERNANCE_CONTROLS_MAX_HEIGHT}
              page={controlsPage}
              pageSize={GOVERNANCE_CONTROLS_PAGE_SIZE}
              totalItems={controls.length}
              onPageChange={setControlsPage}
              itemLabel="controls"
              pagerStyle="simple"
              rowKey={(row, i) => row.control_id ?? `control-${i}`}
            />
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={GOVERNANCE_SECTION_COPY.findings.header}
          rightLabel={`${findings.length} findings`}
        >
          <div className="p-3">
            <DataTable
              columns={findingColumns}
              data={sortedFindings}
              compact
              maxHeight={GOVERNANCE_FINDINGS_MAX_HEIGHT}
              page={findingsPage}
              pageSize={GOVERNANCE_FINDINGS_PAGE_SIZE}
              totalItems={findings.length}
              onPageChange={setFindingsPage}
              itemLabel="findings"
              pagerStyle="simple"
              rowKey={(row, i) =>
                `${row.finding_type ?? "finding"}-${row.affected_control ?? i}`
              }
            />
          </div>
        </SectionCard>
      </div>
    </>
  );
}

interface GovernanceSummaryTilesProps {
  controlsPresent: number;
  controlsExpected: number;
  strongControls: number;
  namedOwners: number;
  reviewCyclesDefined: number;
  overallPct: string;
}

function GovernanceSummaryTiles({
  controlsPresent,
  controlsExpected,
  strongControls,
  namedOwners,
  reviewCyclesDefined,
  overallPct,
}: GovernanceSummaryTilesProps) {
  return (
    <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-3 lg:grid-cols-5">
      <StatTile
        value={`${controlsPresent} / ${controlsExpected}`}
        label="Controls present"
        tone="emerald"
      />
      <StatTile value={strongControls} label="Strong controls" tone="indigo" />
      <StatTile
        value={namedOwners}
        label="Named owners / approvers"
        tone="cyan"
      />
      <StatTile
        value={reviewCyclesDefined}
        label="Review cycles defined"
        tone="amber"
      />
      <StatTile value={overallPct} label="Governance score" tone="emerald" />
    </div>
  );
}
